import { IconButton, PopoverMenu } from '@wix/design-system';
import React from 'react';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import { ArchiveSmall, EditSmall, MoreSmall } from '@wix/wix-ui-icons-common';
import {
  CustomFieldOrigin,
  DataExtensionState,
  withoutDefaults,
} from '@wix/bex-core';
import { cairoCtaClicked } from '@wix/bex-core/bi';
import { observer } from 'mobx-react-lite';
import { CustomFieldsDisabledTooltip } from '../CustomFields';

export interface ManageFieldsPopoverMenuProps {
  state: DataExtensionState;
  id: string;
  origin: CustomFieldOrigin;
  location: string;
}

function _ManageFieldsPopoverMenu({
  state,
  id,
  origin,
  location,
}: ManageFieldsPopoverMenuProps) {
  const container = useWixPatternsContainer();
  const { translate: t } = container;

  if (!state.hasWritePermissions) {
    return (
      <CustomFieldsDisabledTooltip
        disabled={!state.hasWritePermissions}
        content={t('cairo.customFields.writePermission.tooltip')}
      >
        <IconButton size="tiny" dataHook="custom-field-options-trigger">
          <MoreSmall />
        </IconButton>
      </CustomFieldsDisabledTooltip>
    );
  }

  const reportCtaClickedBi = ({
    ctaIndex,
    ctaName,
    moreActionsIndex,
  }: {
    ctaIndex: number;
    ctaName: string;
    moreActionsIndex?: number;
  }) => {
    state?.reportBi(
      withoutDefaults(cairoCtaClicked)({
        ctaIndex,
        ctaName,
        filteredListSize: state?.availableUserCustomFields.length,
        isSelectAll: false,
        location,
        moreActionsIndex,
        numCtas: 2,
        numItems: 1,
      }),
    );
  };

  const onTriggerMenu = () => {
    reportCtaClickedBi({
      ctaIndex: 0,
      ctaName: 'More Actions Menu',
    });
  };

  return (
    <PopoverMenu
      textSize="small"
      placement="bottom-end"
      dataHook="custom-field-options"
      onShow={onTriggerMenu}
      triggerElement={
        <IconButton
          size="tiny"
          dataHook="custom-field-options-trigger"
          skin="light"
        >
          <MoreSmall />
        </IconButton>
      }
    >
      <PopoverMenu.MenuItem
        dataHook="custom-field-options-edit"
        prefixIcon={<EditSmall />}
        text={t('cairo.customFields.edit.CTA')}
        onClick={() => {
          reportCtaClickedBi({
            ctaIndex: 1,
            ctaName: 'Edit',
            moreActionsIndex: 1,
          });
          state?.openCustomFieldModal({
            fieldId: id,
            origin,
          });
        }}
      />

      <PopoverMenu.MenuItem
        dataHook="custom-field-options-archive"
        prefixIcon={<ArchiveSmall />}
        text={t('cairo.customFields.archive.CTA')}
        onClick={() => {
          reportCtaClickedBi({
            ctaIndex: 2,
            ctaName: 'Archive',
            moreActionsIndex: 2,
          });
          state?.archiveFieldModal?.open({ id, origin });
        }}
      />
    </PopoverMenu>
  );
}

export const ManageFieldsPopoverMenu = observer(_ManageFieldsPopoverMenu);
